import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Cpu, Search as SearchIcon, Loader2, GitCompareArrows, HardDrive, MemoryStick, MonitorCog, Network, Usb, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { JsonToggle } from '@/components/common/JsonToggle';
import { Truncated } from '@/components/common/Truncated';
import { hardwareApi } from '@/api/endpoints';
import { formatDate } from '@/lib/utils';
import { toast } from '@/store/toast';

function fmtBytes(n: any): string {
  const v = typeof n === 'number' ? n : Number(n);
  if (!v || isNaN(v) || v <= 0) return '—';
  const gb = v / (1024 ** 3);
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
  const mb = v / (1024 ** 2);
  return `${mb.toFixed(0)} MB`;
}
function val(v: any): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  return String(v);
}

// Tabela chave/valor a partir de um objeto + lista de [rotulo, chave].
function KeyVals({ obj, rows }: { obj: any; rows: [string, string, ((v: any) => string)?][] }) {
  if (!obj) return <div className="text-sm text-muted-foreground">Sem dados</div>;
  return (
    <Table>
      <TableBody>
        {rows.map(([label, key, fmt]) => {
          const raw = obj[key];
          if (raw === undefined || raw === null || raw === '') return null;
          return (
            <TableRow key={key}>
              <TableCell className="font-medium text-muted-foreground w-1/3">{label}</TableCell>
              <TableCell className="font-mono text-xs break-all">{fmt ? fmt(raw) : val(raw)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function SectionCard({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Icon className="h-4 w-4" />{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function RamGauge({ memory }: { memory: any }) {
  if (!memory) return null;
  const pct = typeof memory.usedPercent === 'number' ? memory.usedPercent : 0;
  const tone = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{fmtBytes(memory.usedBytes)} em uso de {fmtBytes(memory.totalBytes)}</span>
        <span className="tabular-nums font-medium">{pct}%</span>
      </div>
      <div className="h-2 w-full rounded bg-muted overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>
    </div>
  );
}

function InventoryView({ doc }: { doc: any }) {
  const os = doc.os ?? {};
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SectionCard title="Sistema" icon={MonitorCog}>
        <KeyVals obj={os} rows={[
          ['Windows', 'productName'],
          ['Edição', 'editionId'],
          ['Versão', 'version'],
          ['Build', 'build'],
          ['UBR', 'ubr'],
          ['Display', 'displayVersion'],
          ['Arquitetura', 'architecture'],
          ['Product ID', 'productId'],
          ['Product Key', 'productKey'],
          ['Canal da Key', 'productKeyChannel'],
          ['Nome da máquina', 'machineName'],
          ['Usuário', 'userName'],
          ['Locale', 'locale'],
          ['Fuso', 'timezone'],
        ]} />
      </SectionCard>

      <SectionCard title="Processador" icon={Cpu}>
        <KeyVals obj={doc.cpu} rows={[
          ['Nome', 'name'], ['Fabricante', 'vendor'], ['Núcleos', 'cores'], ['Threads', 'threads'],
          ['Clock (MHz)', 'mhz'], ['ProcessorId', 'processorId'], ['Soquete', 'socket'], ['Assinatura', 'signature'],
        ]} />
      </SectionCard>

      <SectionCard title="Placa-mãe" icon={MonitorCog}>
        <KeyVals obj={doc.board} rows={[
          ['Fabricante', 'manufacturer'], ['Modelo', 'product'], ['Versão', 'version'], ['Serial', 'serial'],
        ]} />
      </SectionCard>

      <SectionCard title="BIOS / Firmware" icon={ShieldCheck}>
        <KeyVals obj={doc.bios} rows={[
          ['Fabricante', 'vendor'], ['Versão', 'version'], ['Data', 'releaseDate'], ['Serial', 'serial'],
          ['SMBIOS UUID', 'smbiosUuid'], ['Firmware', 'firmwareType'], ['Secure Boot', 'secureBoot'],
        ]} />
      </SectionCard>

      <SectionCard title="TPM" icon={ShieldCheck}>
        <KeyVals obj={doc.tpm} rows={[
          ['Presente', 'present'], ['Spec', 'specVersion'], ['Fabricante', 'manufacturer'],
          ['Versão fab.', 'manufacturerVersion'], ['Habilitado', 'enabled'], ['Ativado', 'activated'],
          ['Owned', 'owned'], ['Hash da chave', 'publicKeyHash'],
        ]} />
      </SectionCard>

      <SectionCard title="Boot" icon={ShieldCheck}>
        <KeyVals obj={doc.boot} rows={[
          ['Modo', 'mode'], ['Secure Boot', 'secureBoot'], ['Serial disco de boot', 'bootDiskSerial'],
        ]} />
      </SectionCard>

      <SectionCard title="Memória RAM" icon={MemoryStick}>
        <RamGauge memory={doc.memory} />
        {Array.isArray(doc.memory?.modules) && doc.memory.modules.length > 0 && (
          <Table className="mt-3">
            <TableHeader><TableRow>
              <TableHead>Slot</TableHead><TableHead>Cap.</TableHead><TableHead>Fab.</TableHead>
              <TableHead>Part #</TableHead><TableHead>Serial</TableHead><TableHead>MHz</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {doc.memory.modules.map((m: any, i: number) => (
                <TableRow key={i} className="font-mono text-xs">
                  <TableCell>{val(m.slot)}</TableCell>
                  <TableCell>{fmtBytes(m.capacityBytes)}</TableCell>
                  <TableCell>{val(m.manufacturer)}</TableCell>
                  <TableCell><Truncated value={val(m.partNumber)} maxChars={18} /></TableCell>
                  <TableCell>{val(m.serial)}</TableCell>
                  <TableCell>{val(m.speedMhz)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <SectionCard title="Discos" icon={HardDrive}>
        <ArrayTable items={doc.disks} cols={[
          ['Modelo', 'model'], ['Serial', 'serial'], ['Tamanho', 'sizeBytes', fmtBytes],
          ['Mídia', 'media'], ['Barramento', 'bus'], ['Interface', 'interface'], ['Firmware', 'firmwareRev'],
        ]} />
      </SectionCard>

      <SectionCard title="Placas de vídeo" icon={MonitorCog}>
        <ArrayTable items={doc.gpu} cols={[
          ['Nome', 'name'], ['Fabricante', 'vendor'], ['Driver', 'driverVersion'],
          ['VRAM', 'vramBytes', fmtBytes], ['VEN', 'vendorId'], ['DEV', 'deviceId'], ['PNP', 'pnpDeviceId'],
        ]} />
      </SectionCard>

      <SectionCard title="Rede" icon={Network}>
        <ArrayTable items={doc.network} cols={[
          ['Descrição', 'description'], ['MAC', 'mac'], ['Tipo', 'type'], ['Físico', 'isPhysical'],
          ['Fabricante', 'manufacturer'], ['IPs', 'ipAddresses', (v: any) => Array.isArray(v) ? v.join(', ') : val(v)],
        ]} />
      </SectionCard>

      <div className="lg:col-span-2">
        <SectionCard title="Dispositivos USB" icon={Usb}>
          <ArrayTable items={doc.usb} cols={[
            ['Descrição', 'description'], ['VID', 'vendorId'], ['PID', 'productId'],
            ['Serial', 'serial'], ['Fabricante', 'manufacturer'], ['Instance ID', 'instanceId'],
          ]} />
        </SectionCard>
      </div>
    </div>
  );
}

function ArrayTable({ items, cols }: { items: any; cols: [string, string, ((v: any) => string)?][] }) {
  if (!Array.isArray(items) || items.length === 0) return <div className="text-sm text-muted-foreground">Nenhum item</div>;
  return (
    <Table>
      <TableHeader><TableRow>{cols.map(([label]) => <TableHead key={label}>{label}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {items.map((it: any, i: number) => (
          <TableRow key={i} className="font-mono text-xs">
            {cols.map(([label, key, fmt]) => (
              <TableCell key={key}><Truncated value={fmt ? fmt(it[key]) : val(it[key])} maxChars={28} /></TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ===== Aba Comparar =====
function CompareTab({ initialA }: { initialA: string }) {
  const [a, setA] = useState(initialA);
  const [b, setB] = useState('');
  const [res, setRes] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(() => {
    if (!a || !b) return;
    setLoading(true);
    hardwareApi.compare(a.trim(), b.trim())
      .then(setRes)
      .catch((e: any) => toast.error('Falha ao comparar', e?.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  }, [a, b]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        <Input placeholder="HWID A" value={a} onChange={e => setA(e.target.value)} className="max-w-md font-mono text-xs" />
        <Input placeholder="HWID B" value={b} onChange={e => setB(e.target.value)} className="max-w-md font-mono text-xs" />
        <Button onClick={run} disabled={!a || !b || loading}>
          {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <GitCompareArrows className="h-3 w-3 mr-1" />} Comparar
        </Button>
      </div>

      {res && !res.bothFound && (
        <EmptyState title="Uma das máquinas não foi encontrada" description="Confira os HWIDs informados." />
      )}

      {res && res.bothFound && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              Componentes em comum
              <Badge variant={res.sharedComponentCount > 0 ? 'destructive' : 'secondary'}>{res.sharedComponentCount}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {res.sharedComponentCount === 0
              ? <div className="text-sm text-muted-foreground">Nenhum identificador de hardware coincide entre as duas máquinas.</div>
              : (
                <Table>
                  <TableBody>
                    {Object.entries(res.matches).map(([k, v]) => {
                      const arr = Array.isArray(v) ? v : null;
                      if (arr && arr.length === 0) return null;
                      if (v === null || v === undefined || v === '') return null;
                      return (
                        <TableRow key={k}>
                          <TableCell className="font-medium text-muted-foreground w-1/3">{k}</TableCell>
                          <TableCell className="font-mono text-xs break-all">
                            {arr ? arr.map((x, i) => <Badge key={i} variant="outline" className="mr-1 mb-1">{String(x)}</Badge>) : String(v)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== Aba Histórico =====
function HistoryTab({ hwid }: { hwid: string }) {
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hwid) return;
    setLoading(true);
    hardwareApi.history(hwid, 1, 50)
      .then(r => { setItems(r.items); setTotal(r.totalApprox); })
      .catch((e: any) => toast.error('Falha ao carregar histórico', e?.message))
      .finally(() => setLoading(false));
  }, [hwid]);

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>;
  if (items.length === 0) return <EmptyState title="Sem histórico" description="Snapshots de hardware aparecem aqui quando o inventário muda." />;

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Snapshots ({total})</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Data</TableHead><TableHead>Hash</TableHead><TableHead>Windows</TableHead>
            <TableHead>RAM</TableHead><TableHead>Discos</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((d, i) => (
              <TableRow key={i} className="text-xs">
                <TableCell className="whitespace-nowrap">{formatDate(d.ts ?? d.capturedAtUtc)}</TableCell>
                <TableCell className="font-mono"><Truncated value={val(d.inventoryHash)} maxChars={12} /></TableCell>
                <TableCell>{val(d.os?.productName)} ({val(d.os?.build)})</TableCell>
                <TableCell>{fmtBytes(d.memory?.totalBytes)}</TableCell>
                <TableCell>{Array.isArray(d.disks) ? d.disks.length : 0}</TableCell>
                <TableCell><JsonToggle data={d} label="JSON" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function HardwarePage() {
  const [params, setParams] = useSearchParams();
  const hwidParam = params.get('hwid') ?? '';
  const [hwidInput, setHwidInput] = useState(hwidParam);
  const [doc, setDoc] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback((hwid: string) => {
    if (!hwid) return;
    setLoading(true);
    setNotFound(false);
    hardwareApi.latest(hwid)
      .then(d => setDoc(d))
      .catch((e: any) => {
        if (e?.response?.status === 404) { setDoc(null); setNotFound(true); }
        else toast.error('Falha ao carregar hardware', e?.message);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (hwidParam) load(hwidParam); }, [hwidParam, load]);

  function submit() {
    const h = hwidInput.trim();
    setParams(h ? { hwid: h } : {});
  }

  return (
    <div>
      <PageHeader
        title="Inventário de Hardware"
        subtitle="Telemetria completa da máquina: SO, dispositivos e seriais — para visualizar, analisar e comparar."
        actions={doc ? <JsonToggle data={doc} label="JSON completo" /> : undefined}
      />

      <Card className="mb-4">
        <CardContent className="pt-4">
          <div className="flex gap-2 flex-wrap items-center">
            <Input
              placeholder="HWID da máquina (V1-…)"
              value={hwidInput}
              onChange={e => setHwidInput(e.target.value)}
              className="max-w-xl font-mono text-xs"
              onKeyDown={e => e.key === 'Enter' && submit()}
            />
            <Button onClick={submit} disabled={!hwidInput.trim() || loading}>
              {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <SearchIcon className="h-3 w-3 mr-1" />} Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading && !doc && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>}
      {notFound && <EmptyState icon={Cpu} title="Máquina não encontrada" description="Nenhum inventário de hardware para esse HWID ainda." />}
      {!hwidParam && !doc && <EmptyState icon={Cpu} title="Informe um HWID" description="Digite o HWID para ver o inventário, ou use a aba Comparar." />}

      <Tabs defaultValue="overview" className="mt-2">
        <TabsList>
          <TabsTrigger value="overview">Visão geral</TabsTrigger>
          <TabsTrigger value="history" disabled={!hwidParam}>Histórico</TabsTrigger>
          <TabsTrigger value="compare">Comparar</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4">
          {doc && (
            <>
              <div className="mb-3 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="font-mono">{val(doc.hardwareId)}</span>
                {doc.updatedAt && <span>Atualizado: {formatDate(doc.updatedAt)}</span>}
              </div>
              <InventoryView doc={doc} />
            </>
          )}
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          {hwidParam && <HistoryTab hwid={hwidParam} />}
        </TabsContent>
        <TabsContent value="compare" className="mt-4">
          <CompareTab initialA={hwidParam} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
